// All data extracted from "ERP Implementation Guidance for Gigmann Medicals Limited" PDF
// Used as the canonical spec for Gr8Boost ERP with Gr8Agentic AI

export const executiveSummary = {
  product: "Gr8Boost ERP with Gr8Agentic AI",
  tagline: "A white-label cloud ERP, supercharged by Gr8Agentic AI — the AI co-pilot for medical distribution, engineering services & operations.",
  clientCase: "Gigmann Medicals Limited (Ghana, est. 2022) — medical consumables supply, equipment distribution, and engineering & technical services.",
  problem: [
    { title: "Manual & paper-driven processes", detail: "Slow operations, data duplication, lost documents." },
    { title: "Lack of process discipline", detail: "ERP bypassing, incorrect data entry." },
    { title: "Data inaccuracy & errors", detail: "Fragmentation causing mismatches and reconciliation pain." },
    { title: "Limited visibility & reporting", detail: "No real-time dashboards; delayed decisions." },
    { title: "Scalability constraints", detail: "Manual workflows bottleneck growth." },
    { title: "Internal controls gap & fraud risk", detail: "Untracked processes increase exposure." },
    { title: "Service & technical complexity", detail: "Tracking jobs, parts, warranties, SLAs." },
    { title: "Customer experience gaps", detail: "Complaints, returns, warranties poorly tracked." },
    { title: "Budgeting & cost control", detail: "Tedious without integrated data." },
  ],
  outcomes: [
    "Automated operations across all departments",
    "Stronger compliance & reduced fraud exposure",
    "Real-time KPI dashboards for leadership",
    "Scalable foundation for multi-entity growth",
    "Cross-department collaboration as the default",
  ],
};

export const phases = [
  {
    id: "planning",
    number: "01",
    name: "Planning & Preparation",
    duration: "4–6 weeks",
    status: "In progress",
    activities: [
      "Define ERP objectives and success criteria",
      "Establish implementation governance structure",
      "Finalize ERP scope (modules, departments, workflows)",
      "Conduct detailed requirements gathering",
      "Prepare budget and resource plan",
      "Develop high-level project plan with milestones",
    ],
    deliverables: [
      "Signed-off objectives & success criteria",
      "Governance charter (steering committee, RACI)",
      "Scope document",
      "Requirements catalogue",
      "Budget & resource plan",
      "High-level Gantt with milestones",
    ],
    parties: ["COO / CEO", "ERP Implementation Team", "External Advisor"],
  },
  {
    id: "blueprinting",
    number: "02",
    name: "System Design & Blueprinting",
    duration: "6–8 weeks",
    status: "In progress",
    activities: [
      "Translate workflows and controls into ERP design",
      "Map roles, approvals, and responsibilities in the ERP",
      "Identify necessary customisations",
      "Review and validate design with department heads",
    ],
    deliverables: [
      "ERP design blueprint",
      "Role & approval matrix",
      "Customisation register",
      "Department sign-off log",
    ],
    parties: ["ERP Implementation Team", "All Department Heads", "COO / CEO", "Advisor"],
  },
  {
    id: "configuration",
    number: "03",
    name: "Configuration & Development",
    duration: "8–12 weeks",
    status: "Upcoming",
    activities: [
      "Configure ERP modules (Finance, Sales, Inventory, Procurement, HR, Engineering)",
      "Implement user roles and access controls",
      "Develop necessary customisations",
      "Integrate ERP with existing systems (email, IT inventory)",
      "Set up reporting dashboards",
    ],
    deliverables: [
      "Configured production tenant",
      "RBAC implementation",
      "Custom workflows & forms",
      "Integration endpoints live",
      "Executive dashboards",
    ],
    parties: ["ERP Implementation Team", "IT & Systems Management", "Advisor"],
  },
  {
    id: "testing",
    number: "04",
    name: "Testing & Validation",
    duration: "4–6 weeks",
    status: "Upcoming",
    activities: [
      "Conduct unit testing (per module)",
      "Perform integrated testing across departments",
      "Validate internal controls (approvals, access restrictions)",
      "Simulate key processes (sales, procurement, payroll)",
      "Capture and resolve issues before rollout",
    ],
    deliverables: [
      "Unit test report",
      "End-to-end UAT sign-off",
      "Controls validation report",
      "Defect log → zero P0/P1",
    ],
    parties: ["ERP Implementation Team", "Department Heads", "Finance & Internal Audit", "Advisor"],
  },
  {
    id: "training",
    number: "05",
    name: "Training & Change Management",
    duration: "3–5 weeks",
    status: "Upcoming",
    activities: [
      "Develop training materials and user guides",
      "Train end-users by department / function",
      "Conduct hands-on practice sessions",
      "Communicate benefits to reduce resistance",
      "Identify ERP Champions within departments",
    ],
    deliverables: [
      "Training portal & video library",
      "Role-based user guides",
      "Champions network roster",
      "Communication plan",
    ],
    parties: ["ERP Implementation Team", "HR & Admin", "Department Heads", "Advisor"],
  },
  {
    id: "golive",
    number: "06",
    name: "Rollout & Go-Live",
    duration: "2–4 weeks + hypercare",
    status: "Upcoming",
    activities: [
      "Deploy ERP system company-wide",
      "Transition from manual to ERP-driven processes",
      "Monitor live transactions for errors",
      "Provide on-site / remote support",
      "Conduct first post-implementation review",
    ],
    deliverables: [
      "Production cutover",
      "Hypercare desk (2 weeks min)",
      "Post-implementation review report",
      "Stabilization KPI snapshot",
    ],
    parties: ["ERP Implementation Team", "CEO / COO", "Internal Audit", "Advisor"],
  },
];

export const modules = [
  {
    id: "finance",
    name: "Finance & Accounts",
    icon: "Wallet",
    workflows: [
      "Receivables Management",
      "Payables Management",
      "Bank Reconciliation",
      "Financial Reporting",
      "Budget Preparation & Monitoring",
      "Customer Refunds",
      "Tax Accounting & Remittance",
      "Petty Cash Management",
      "Internal Expenses (Opex/Capex)",
      "Fixed Asset Accounting",
      "Loan Repayment",
    ],
    controls: [
      "Segregation of duties (initiator vs. approver)",
      "Multi-tier payment approvals",
      "Auto bank-feed reconciliation",
      "Locked posting periods after close",
      "Audit trail on every journal",
    ],
    configRequirements: [
      "Chart of accounts mapped to Ghana CIT/VAT structure",
      "AR aging buckets (0-30/31-60/61-90/90+)",
      "Approval workflows by amount thresholds",
      "Integrated tax module (VAT, NHIL, GETFund)",
      "Sales module integration for real-time AR updates",
    ],
  },
  {
    id: "engineering",
    name: "Engineering & Technical Services",
    icon: "Wrench",
    workflows: [
      "Job Request Logging (External)",
      "Job Assignment & Scheduling",
      "Installation of Equipment",
      "Internal Equipment Setup",
      "Planned Maintenance Scheduling",
      "Breakdown & Repair Handling",
      "Spare Parts Request & Usage",
      "Warranty & Service Contract Tracking",
      "Job Completion & Client Sign-Off",
      "Technician Performance Monitoring",
      "Technical Training Programs (External)",
      "Internal Training for Technicians",
      "Field Report & Documentation Upload",
      "Asset & Tool Tracking for Engineers",
      "IMART Job Orders",
    ],
    controls: [
      "Job orders cannot close without client sign-off",
      "Parts issued only against an open job",
      "Warranty status checked before billing",
      "Photo / document evidence mandatory on completion",
    ],
    configRequirements: [
      "Mobile field-service app for technicians",
      "Link job orders to sales order / asset",
      "Geo-stamped check-in / check-out",
      "SLA timers and escalation rules",
      "Spare-parts BOM per equipment model",
    ],
  },
  {
    id: "hr",
    name: "HR & Administration",
    icon: "Users",
    workflows: [
      "Employee Onboarding",
      "Employee Offboarding",
      "Leave Management",
      "Attendance & Time Tracking",
      "Payroll Processing",
      "Performance Appraisal",
      "Recruitment & Job Applications",
      "Training & Development Tracking",
      "Asset Issuance to Employees",
      "Facility & Utility Management",
      "Visitor Management & Security",
      "Document Management",
    ],
    controls: [
      "Statutory deductions auto-calculated (SSNIT, PAYE)",
      "Leave balances enforced before approval",
      "Offboarding auto-deactivates ERP & email accounts",
      "Performance reviews linked to bonus payroll runs",
    ],
    configRequirements: [
      "Biometric attendance integration",
      "Auto-provision/deprovision of email & ERP login",
      "Payroll calendars & tax bands",
      "Document repository with version control",
      "Org chart & reporting lines",
    ],
  },
  {
    id: "sales",
    name: "Sales",
    icon: "ShoppingCart",
    workflows: [
      "Customer Registration",
      "Sales Quotation / Proforma Invoice",
      "Sales Order Creation",
      "Order Approval",
      "Goods Delivery & GDN Issuance",
      "Sales Invoice Generation",
      "Customer Payment Collection",
      "Customer Returns / Credit Notes",
      "Customer Statements & Aging",
      "Walk-in Customers (POS)",
      "Emergency Sale",
      "Purchase Order / B2B Customers",
    ],
    controls: [
      "Credit limit check before order release",
      "Pricing locked to approved price list",
      "Discounts above threshold require approval",
      "Returns matched to original invoice",
    ],
    configRequirements: [
      "POS module for walk-in customers",
      "B2B portal with PO upload",
      "Sales order → GDN → invoice linkage",
      "Customer aging dashboards",
      "Mobile sales-rep app",
    ],
  },
  {
    id: "procurement",
    name: "Procurement & Vendor Management",
    icon: "Truck",
    workflows: [
      "Vendor Registration & Approval",
      "Purchase Requisition (PR)",
      "Request for Quotation (RFQ)",
      "Quotation Comparison & Selection",
      "Purchase Order (PO) Creation",
      "Goods Receipt & GRN Issuance",
      "Invoice Matching & Verification",
      "Procurement Approvals & Limits",
      "Supplier Performance Review",
    ],
    controls: [
      "Three-way match: PO ↔ GRN ↔ Invoice",
      "Vendor due-diligence checklist before approval",
      "Buyer cannot approve their own PO",
      "Tiered approval by amount",
    ],
    configRequirements: [
      "Vendor portal for RFQ responses",
      "Auto-comparison matrix for quotations",
      "Supplier scorecards (on-time delivery, quality)",
      "PR → PO conversion with budget check",
    ],
  },
  {
    id: "inventory",
    name: "Inventory & Warehouse Management",
    icon: "Package",
    workflows: [
      "Goods Received / Inward Delivery",
      "Inventory Putaway",
      "Inventory Transfer",
      "Stock Issue / Picking",
      "Cycle Counting / Stock Audits",
      "Damaged / Expired Goods Handling",
      "Return to Vendor",
      "Inventory Reconciliation",
    ],
    controls: [
      "Batch & expiry tracking (critical for medical SKUs)",
      "Negative stock disallowed",
      "Cycle-count variance threshold triggers audit",
      "Damaged goods require disposal approval",
    ],
    configRequirements: [
      "Barcode / QR scanning",
      "Multi-warehouse, multi-bin layout",
      "FEFO (first-expired-first-out) pick logic",
      "Reorder-point alerts",
      "Cold-chain monitoring hooks",
    ],
  },
  {
    id: "crm",
    name: "Customer Relationship Management",
    icon: "HeartHandshake",
    workflows: [
      "Sales Enquiry Handling",
      "Complaint Logging",
      "Complaint Escalation & Resolution",
      "Customer Feedback Collection",
      "Post-Sales Follow-Up",
      "Return & Refund Processing (Initiation)",
      "Service Request Intake",
      "Client Relationship Management",
      "Knowledge Base & FAQ Management",
      "Service Quality Monitoring",
      "Interdepartmental Feedback Loop",
      "Customer Account Updates",
    ],
    controls: [
      "All complaints get a ticket number",
      "SLA timers with auto-escalation",
      "Customer 360 visible only to assigned reps",
      "Resolution requires customer confirmation",
    ],
    configRequirements: [
      "Unified customer 360 view",
      "Ticket / case management",
      "Sales → CRM → Finance handoff",
      "Knowledge base with search",
      "NPS & CSAT capture",
    ],
  },
  {
    id: "it",
    name: "IT & Systems Management",
    icon: "ServerCog",
    workflows: [
      "ERP User Account Setup",
      "ERP Role & Access Review",
      "ERP System Maintenance",
      "Incident Reporting & Resolution",
      "Asset Register & IT Inventory",
      "Email & System Account Setup",
      "Data Backup & Recovery",
      "Cybersecurity & Access Logs Monitoring",
      "ERP Customisation & Module Configuration",
      "Software License Management",
      "IT Policy Compliance Monitoring",
    ],
    controls: [
      "Quarterly access reviews mandatory",
      "Auto-deactivation on HR exit",
      "Daily encrypted backups, monthly restore test",
      "Privileged actions logged & alerted",
    ],
    configRequirements: [
      "Single sign-on (SSO)",
      "Role-based access control (RBAC)",
      "Audit log dashboard",
      "Backup & DR scheduler",
      "License & subscription tracker",
    ],
  },
];

export const gr8agenticCapabilities = [
  {
    id: "nl-query",
    title: "Natural-Language Query",
    description: "Ask Gr8Boost ERP anything in plain English — Gr8Agentic AI writes the query, runs it, and explains the answer.",
    example: '"Show me overdue invoices above GHS 50,000 aged more than 60 days, grouped by sales rep."',
    icon: "MessageSquare",
  },
  {
    id: "doc-extract",
    title: "Document & Invoice Extraction",
    description: "Drop a supplier invoice, GRN, or warranty card — Gr8Agentic AI extracts line items and posts them with three-way match.",
    example: "OCR + AI parsing → ERP draft in under 8 seconds. Reviewer approves with one click.",
    icon: "FileText",
  },
  {
    id: "predictive",
    title: "Predictive Insights",
    description: "Forecast sales, cashflow, and stock requirements using historical patterns and seasonality.",
    example: '"You\'ll run out of XYZ syringes in 11 days — auto-draft a PO to Vendor ABC?"',
    icon: "TrendingUp",
  },
  {
    id: "anomaly",
    title: "Anomaly & Fraud Detection",
    description: "Gr8Agentic AI watches every transaction and flags duplicate vendors, unusual discounts, after-hours edits, and policy breaches.",
    example: '"3 invoices from same vendor with identical amounts in 24h — flagged for finance review."',
    icon: "ShieldAlert",
  },
  {
    id: "automation",
    title: "Workflow Automation Co-Pilot",
    description: "Gr8Agentic AI suggests automations based on user behaviour and configures them with a single click.",
    example: '"You manually email 12 customer statements every Monday — want me to schedule it?"',
    icon: "Zap",
  },
];

export const whiteLabelFeatures = [
  { title: "Multi-tenant isolation", detail: "Every customer's data lives in a logically isolated tenant with row-level security." },
  { title: "Per-tenant branding", detail: "Logo, brand colors, favicon, and email templates configurable per tenant." },
  { title: "Custom domain", detail: "yourcompany.gr8boost.app or full vanity domain with managed TLS." },
  { title: "Module toggles", detail: "Activate only the modules each tenant needs — pay for what you use." },
  { title: "Localized tax & currency", detail: "Out-of-the-box support for Ghana (GHS, VAT/NHIL/GETFund) and extendable to other African markets." },
  { title: "Reseller / partner mode", detail: "Implementation partners onboard their own customers and manage them from a partner console." },
];

export const integrations = [
  "Bank feeds (GCB, Ecobank, Stanbic)",
  "Payment gateways (Paystack, Flutterwave, MTN MoMo)",
  "Biometric attendance devices",
  "Field-service mobile apps",
  "Email & calendar (Google, Microsoft 365)",
  "SMS providers (Twilio, Hubtel)",
  "Document storage (Google Drive, S3)",
  "Tax authority (GRA e-filing)",
  "Cold-chain IoT sensors",
  "Single Sign-On (SAML, OAuth)",
  "Power BI / Looker export",
  "WhatsApp Business API",
];

export const roles = [
  { role: "CEO / COO", access: "Full read, approval authority above threshold, executive dashboards" },
  { role: "Department Heads", access: "Read/write within department, approve up to limit" },
  { role: "Finance Manager", access: "All Finance modules, journal posting, period close" },
  { role: "Sales Manager / Reps", access: "Sales orders, customer 360, quotations" },
  { role: "Procurement Manager", access: "PR/PO, RFQ, vendor master" },
  { role: "Inventory / Warehouse", access: "GRN, transfers, cycle counts, stock adjustments" },
  { role: "Technician (Engineering)", access: "Mobile app: job orders, parts, sign-off" },
  { role: "HR / Admin", access: "Employee master, payroll, leave, visitor mgmt" },
  { role: "IT Lead", access: "User provisioning, role review, system admin" },
  { role: "Internal Audit", access: "Read-only across all modules, audit-log access" },
  { role: "Employee (self-service)", access: "Own leave, payslips, expense claims" },
];

export const risks = [
  { risk: "User resistance & low adoption", mitigation: "ERP Champions network, role-based training, executive sponsorship." },
  { risk: "Data migration errors", mitigation: "Dedicated cleansing sprint; dual-run before cutover." },
  { risk: "Scope creep during configuration", mitigation: "Frozen blueprint sign-off; formal change-request process." },
  { risk: "Process bypass post go-live", mitigation: "Internal-audit dashboard; Gr8Agentic AI anomaly alerts." },
  { risk: "Integration failures (banks, biometric)", mitigation: "Plan, resource & test external systems early — not a go-live activity." },
  { risk: "Inadequate post-go-live support", mitigation: "2-week hypercare desk + tier-2 escalation to ERP team." },
];

export const kpis = [
  { dept: "Finance", kpi: "Days Sales Outstanding (DSO)", target: "< 45 days" },
  { dept: "Finance", kpi: "Month-end close cycle", target: "≤ 5 working days" },
  { dept: "Sales", kpi: "Quote-to-order conversion", target: "> 35%" },
  { dept: "Sales", kpi: "On-time delivery rate", target: "> 95%" },
  { dept: "Procurement", kpi: "PO cycle time", target: "≤ 7 days" },
  { dept: "Inventory", kpi: "Stock accuracy", target: "> 98%" },
  { dept: "Inventory", kpi: "Expired stock value", target: "< 0.5% of inventory" },
  { dept: "Engineering", kpi: "First-time-fix rate", target: "> 85%" },
  { dept: "Engineering", kpi: "Avg. job turnaround", target: "≤ 48 hours" },
  { dept: "CRM", kpi: "Customer complaint resolution SLA", target: "> 90%" },
  { dept: "HR", kpi: "Payroll accuracy", target: "100%" },
  { dept: "IT", kpi: "ERP uptime", target: "≥ 99.5%" },
];

export const kpiTrend = [
  { month: "M1", dso: 68, accuracy: 82, adoption: 30 },
  { month: "M2", dso: 62, accuracy: 87, adoption: 48 },
  { month: "M3", dso: 55, accuracy: 91, adoption: 65 },
  { month: "M4", dso: 49, accuracy: 94, adoption: 78 },
  { month: "M5", dso: 44, accuracy: 96, adoption: 88 },
  { month: "M6", dso: 41, accuracy: 98, adoption: 94 },
];
